import * as React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Trash } from "lucide-react";
import { authClient } from '@/lib/auth-client';
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

interface DeleteApiKeyDialogProps {
  apiKeyId: string;
  onDeleted?: () => void; // Make onDeleted optional or provide a default no-op
}

export const DeleteApiKeyDialog: React.FC<DeleteApiKeyDialogProps> = ({ apiKeyId, onDeleted = () => {} }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await authClient.apiKey.delete({ keyId: id });
      if (error) {
        throw new Error(error.message || t('settings.apiKeys.deleteErrorToast', 'Failed to delete API Key'));
      }
    },
    onSuccess: () => {
      toast.success(t('settings.apiKeys.deleteSuccessToast', 'API Key deleted successfully.'));
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      onDeleted();
      setIsOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setIsOpen(false);
    },
  });

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(apiKeyId);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => e.preventDefault()}
          className="text-destructive focus:text-destructive"
        >
          <Trash className="mr-2 h-4 w-4" />
          <span>{t('settings.apiKeys.deleteAction', 'Delete')}</span>
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('settings.apiKeys.deleteConfirmTitle', 'Are you sure?')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('settings.apiKeys.deleteConfirmDescription', 'This action cannot be undone. This will permanently delete the API key.')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};